import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const words = ["Innovate", "Transform", "Grow", "Celebrate"];

const slides = [
  {
    heading: "Empowering Transformation Through People",
    subtext: "Delivering intelligent HR & business solutions for a smarter, connected future.",
    highlight: "Infinite Recruiting At Your Fingertips.",
    video: "/hero.mp4"
  },
  {
    heading: "Building Agile Workforces for Tomorrow",
    subtext: "Providing scalable HR consulting and strategic partnerships for global enterprises.",
    highlight: "Global Talent Solutions, Local Expertise.",
    video: "/herovideo.mp4"
  },
  {
    heading: "Human Potential, Elevated by Innovation",
    subtext: "Transforming talent acquisition through data, AI, and human insight.",
    highlight: "Digital First HR Ecosystems.",
    video: "/hero.mp4"
  },
];

export default function HeroAnimation() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [displayWord, setDisplayWord] = useState(words[0]);
  const [wordIndex, setWordIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Slide Auto-play
  useEffect(() => {
    if (!mounted) return;
    const slideInterval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 8000);
    return () => clearInterval(slideInterval);
  }, [mounted]);

  // Typing Effect
  useEffect(() => {
    if (!mounted) return;
    const currentWord = words[wordIndex];
    const speed = isDeleting ? 70 : 130;
    const typing = setTimeout(() => {
      if (!isDeleting) {
        if (displayWord.length < currentWord.length) {
          setDisplayWord(currentWord.slice(0, displayWord.length + 1));
        } else {
          setTimeout(() => setIsDeleting(true), 2500);
        }
      } else {
        if (displayWord.length > 0) {
          setDisplayWord(currentWord.slice(0, displayWord.length - 1));
        } else {
          setIsDeleting(false);
          setWordIndex((prev) => (prev + 1) % words.length);
        }
      }
    }, speed);
    return () => clearTimeout(typing);
  }, [displayWord, isDeleting, wordIndex, mounted]);



  if (!mounted) return <div className="h-screen w-full bg-slate-900" />;

  return (
    <section className="relative w-full h-screen overflow-hidden bg-slate-950 font-poppins">
      
      {/* BACKGROUND VIDEO WITH OVERLAY */}
      <div className="absolute inset-0">
        <AnimatePresence mode="wait">
          <motion.div
            key={`bg-${currentSlide}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.5 }}
            className="absolute inset-0"
          >
            <video
              autoPlay
              muted
              loop
              playsInline
              className="w-full h-full object-cover"
            >
              <source src={slides[currentSlide].video} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </motion.div>
        </AnimatePresence>
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/30 to-black/60" />
        <div className="absolute inset-0 bg-black/20" /> {/* Extra layer for text readability */}
      </div>

      <div className="relative h-full w-full max-w-[1440px] mx-auto px-6 md:px-16 flex flex-col justify-center">
        
        {/* MAIN CONTENT GRID */}
        <div className="grid lg:grid-cols-2 gap-12 items-center mt-20">
          
          {/* LEFT CONTENT: Heading */}
          <div className="max-w-2xl">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex items-center gap-3 mb-6"
            >
              <span className="text-[#70be44] font-bold tracking-[0.3em] uppercase text-xs md:text-sm">
                We {displayWord}
              </span>
            </motion.div>

            <AnimatePresence mode="wait">
              <motion.h1
                key={currentSlide}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                className="text-4xl md:text-6xl lg:text-7xl font-bold text-white leading-[1.1] mb-8"
              >
                {slides[currentSlide].heading}
              </motion.h1>
            </AnimatePresence>
          </div>

          {/* RIGHT CONTENT: Highlight & CTA */}
          <div className="lg:justify-self-end flex flex-col items-start lg:items-end text-left lg:text-right max-w-md self-end mb-16 lg:mb-20">
            <AnimatePresence mode="wait">
              <motion.div
                key={`right-${currentSlide}`}
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <div className="flex items-center lg:justify-end gap-3 mb-4">
                   <div className="h-24 w-[3px] bg-cyan-400 hidden lg:block" />
                   <h2 className="text-2xl md:text-4xl font-bold text-white leading-tight">
                    {slides[currentSlide].highlight || "Infinite Potential At Your Fingertips."}
                   </h2>
                   <div className="h-16 w-[3px] bg-cyan-400 lg:hidden" />
                </div>
                
                <p className="text-gray-100 text-sm md:text-lg mb-8 max-w-sm ml-auto font-medium drop-shadow-lg">
                  {slides[currentSlide].subtext}
                </p>


              </motion.div>
            </AnimatePresence>
          </div>
        </div>



      </div>

      {/* Background Decorative Blur Orbs */}
      <div className="absolute top-1/4 right-0 w-96 h-96 bg-[#70be44]/20 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-blue-600/10 rounded-full blur-[150px] pointer-events-none" />
    </section>
  );
}